import React from 'react';

import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import i18n from 'i18n-js';
import {
    Button,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
    Box,
    Grid,
} from '@material-ui/core';

import { useStyles } from '../styles';
import Input from 'components/shared/components/Input';
import agent from 'api/agent';
import {
    RestaurantPaymentProvider,
    RestaurantPaymentSettings,
} from 'store/restaurantsStore/reducer';
import { useDispatch } from 'react-redux';
import globalStore from 'store/globalStore';
import restaurantsStore from 'store/restaurantsStore';
import { useHistory } from 'react-router-dom';
import { regexHelper } from 'helpers/regexHelper';

type FieldName = 'scope' | 'identity' | 'secret';

const CUSTOM_PROVIDER = 'CUSTOM';

const providerFieldMap: Record<RestaurantPaymentProvider, Record<FieldName, string>> = {
    [RestaurantPaymentProvider.Wallee]: {
        scope: 'Space Id',
        identity: 'Application User Id',
        secret: 'Authentication Key',
    },
    [RestaurantPaymentProvider.Datatrans]: {
        scope: 'Scope',
        identity: 'Username',
        secret: 'Password',
    },
    [RestaurantPaymentProvider.PayTec]: {
        scope: 'Scope',
        identity: 'Identity',
        secret: 'Secret',
    },
    [RestaurantPaymentProvider.Softpay]: {
        scope: 'Scope',
        identity: 'Identity',
        secret: 'Secret',
    },
};

interface ProviderFields {
    providerName: string;
    customProviderName?: string;
    scope?: string;
    identity: string;
    secret: string;
}

interface FormValues {
    posProvider: ProviderFields;
    webProvider: ProviderFields;
}

const providerValidation = Yup.object().shape({
    providerName: Yup.string().required('Provider is required'),
    customProviderName: Yup.string().when('providerName', (providerName: string, schema: any) => {
        if (providerName === CUSTOM_PROVIDER) {
            return schema
                .required(
                    i18n.t('form.errors.required', {
                        name: i18n.t('form.customProviderName'),
                    })
                )
                .trim()
                .min(1, i18n.t('form.errors.required', { name: i18n.t('form.customProviderName') }))
                .max(
                    50,
                    i18n.t('form.errors.maxLength', {
                        name: i18n.t('form.customProviderName'),
                        length: 50,
                    })
                )
                .matches(
                    regexHelper.numbersAndAllLetters,
                    i18n.t('form.errors.lettersAndNumbersOnly')
                );
        }
        return schema.notRequired();
    }),
    scope: Yup.string().when(
        'providerName',
        (providerName: RestaurantPaymentProvider, schema: any) => {
            if (providerName === RestaurantPaymentProvider.Wallee) {
                return schema
                    .required(
                        i18n.t('form.errors.required', {
                            name: providerFieldMap[providerName].scope,
                        })
                    )
                    .trim()
                    .min(
                        1,
                        i18n.t('form.errors.required', {
                            name: providerFieldMap[providerName].scope,
                        })
                    );
            }
            return schema.notRequired();
        }
    ),
    identity: Yup.string().when(
        'providerName',
        (providerName: RestaurantPaymentProvider, schema: any) => {
            if (
                providerName === RestaurantPaymentProvider.Datatrans ||
                providerName === RestaurantPaymentProvider.Wallee
            ) {
                return schema
                    .required(
                        i18n.t('form.errors.required', {
                            name: providerFieldMap[providerName].identity,
                        })
                    )
                    .trim()
                    .min(
                        1,
                        i18n.t('form.errors.required', {
                            name: providerFieldMap[providerName].identity,
                        })
                    );
            }
            return schema.notRequired();
        }
    ),
    secret: Yup.string().when(
        'providerName',
        (providerName: RestaurantPaymentProvider, schema: any) => {
            if (
                providerName === RestaurantPaymentProvider.Datatrans ||
                providerName === RestaurantPaymentProvider.Wallee
            ) {
                return schema
                    .required(
                        i18n.t('form.errors.required', {
                            name: providerFieldMap[providerName].secret,
                        })
                    )
                    .trim()
                    .min(
                        1,
                        i18n.t('form.errors.required', {
                            name: providerFieldMap[providerName].secret,
                        })
                    );
            }
            return schema.notRequired();
        }
    ),
});

const webProviderValidation = providerValidation.clone().shape({
    providerName: Yup.string().notRequired(),
});

const validationSchema = Yup.object().shape({
    posProvider: providerValidation,
    webProvider: webProviderValidation,
});

interface PaymentProvidersProps {
    restaurantId: string;
    paymentProviders?: RestaurantPaymentSettings;
}

export default function PaymentProviders(props: PaymentProvidersProps) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();

    function getFieldLabel(provider: RestaurantPaymentProvider, field: FieldName): string {
        return providerFieldMap[provider][field];
    }

    function preparePOSProviderName(providerName?: string): string {
        if (!providerName) {
            return RestaurantPaymentProvider.Wallee;
        } else if (
            providerName !== CUSTOM_PROVIDER &&
            Object.values(RestaurantPaymentProvider).includes(
                providerName as RestaurantPaymentProvider
            )
        ) {
            return providerName as RestaurantPaymentProvider;
        } else {
            return CUSTOM_PROVIDER;
        }
    }

    const initialValues: FormValues = {
        posProvider: {
            providerName: preparePOSProviderName(
                props.paymentProviders?.posPaymentProvider?.providerName
            ),
            customProviderName: props.paymentProviders?.posPaymentProvider?.providerName,
            scope: props.paymentProviders?.posPaymentProvider?.credentials?.scope || '',
            identity: props.paymentProviders?.posPaymentProvider?.credentials?.identity || '',
            secret: props.paymentProviders?.posPaymentProvider?.credentials?.secret || '',
        },
        webProvider: {
            providerName:
                props.paymentProviders?.webPaymentProvider?.providerName ||
                ('' as RestaurantPaymentProvider),
            scope: props.paymentProviders?.webPaymentProvider?.credentials?.scope || '',
            identity: props.paymentProviders?.webPaymentProvider?.credentials?.identity || '',
            secret: props.paymentProviders?.webPaymentProvider?.credentials?.secret || '',
        },
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={(values, { setSubmitting }) => {
                const model: RestaurantPaymentSettings = {
                    posPaymentProvider: {
                        providerName:
                            values.posProvider.customProviderName ||
                            values.posProvider.providerName,
                        isCustomProvider: values.posProvider.providerName === CUSTOM_PROVIDER,
                        credentials: {
                            scope: values.posProvider.scope || '',
                            identity: values.posProvider.identity,
                            secret: values.posProvider.secret,
                        },
                    },
                    webPaymentProvider: {
                        providerName: values.webProvider.providerName,
                        isCustomProvider: values.webProvider.providerName === CUSTOM_PROVIDER,
                        credentials: {
                            scope: values.webProvider.scope || '',
                            identity: values.webProvider.identity,
                            secret: values.webProvider.secret,
                        },
                    },
                };
                dispatch(globalStore.actionCreators.showSpiner());
                agent.Restaurants.SetPaymentSettings(props.restaurantId, model)
                    .then((res) => {
                        dispatch(
                            restaurantsStore.actionCreators.setPaymentSettings(
                                props.restaurantId,
                                res
                            )
                        );
                        dispatch(
                            globalStore.actionCreators.showToaster(
                                'success',
                                i18n.t('messages.updatedSuccesfully')
                            )
                        );
                        history.push(`/restaurants`);
                    })
                    .catch((e) => {
                        dispatch(globalStore.actionCreators.showToaster('error', e));
                    })
                    .finally(() => {
                        dispatch(globalStore.actionCreators.hideSpiner());
                        setSubmitting(false);
                    });
            }}
        >
            {({
                values,
                handleChange,
                handleBlur,
                touched,
                errors,
                isValid,
                isSubmitting,
                dirty,
                setFieldValue,
                setValues,
                setFieldTouched,
                validateForm,
            }) => {
                const handleProviderChange =
                    (section: 'posProvider' | 'webProvider') =>
                    async (event: React.ChangeEvent<{ value: unknown }>) => {
                        const providerName = event.target.value as RestaurantPaymentProvider;
                        setFieldValue(`${section}.providerName`, providerName);
                        setFieldTouched(`${section}.providerName`, true);

                        setFieldValue(
                            `${section}.customProviderName`,
                            initialValues[section].customProviderName
                        );
                        setFieldValue(`${section}.scope`, '');
                        setFieldValue(`${section}.identity`, '');
                        setFieldValue(`${section}.secret`, '');

                        setTimeout(() => {
                            validateForm();
                        }, 0);
                    };

                return (
                    <Form className={classes.formInnerContainer} style={{ width: 700 }}>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6}>
                                <h6 className={classes.sectionTitle}>
                                    {i18n.t('restaurant.posProvider').toUpperCase()}
                                </h6>
                                <Box mb={2} />
                                <FormControl
                                    className={classes.inputContainer}
                                    error={Boolean(
                                        touched.posProvider?.providerName &&
                                            errors.posProvider?.providerName
                                    )}
                                    fullWidth
                                >
                                    <InputLabel
                                        id="pos-provider-label"
                                        style={{ marginLeft: 12, marginTop: 5 }}
                                    >
                                        {i18n.t('form.provider')}
                                    </InputLabel>
                                    <Select
                                        variant="filled"
                                        labelId="pos-provider-label"
                                        id="posProvider.providerName"
                                        name="posProvider.providerName"
                                        value={values.posProvider.providerName}
                                        onChange={handleProviderChange('posProvider')}
                                        onBlur={handleBlur}
                                        autoWidth
                                    >
                                        <MenuItem value={RestaurantPaymentProvider.Wallee}>
                                            {RestaurantPaymentProvider.Wallee}
                                        </MenuItem>
                                        <MenuItem value={RestaurantPaymentProvider.Datatrans}>
                                            {RestaurantPaymentProvider.Datatrans}
                                        </MenuItem>
                                        <MenuItem value={RestaurantPaymentProvider.PayTec}>
                                            {RestaurantPaymentProvider.PayTec}
                                        </MenuItem>
                                        <MenuItem value={RestaurantPaymentProvider.Softpay}>
                                            {RestaurantPaymentProvider.Softpay}
                                        </MenuItem>
                                        <MenuItem value={CUSTOM_PROVIDER}>
                                            {CUSTOM_PROVIDER}
                                        </MenuItem>
                                    </Select>
                                    <FormHelperText>
                                        {errors.posProvider?.providerName}
                                    </FormHelperText>
                                </FormControl>

                                {values.posProvider.providerName === CUSTOM_PROVIDER && (
                                    <Input
                                        variant="filled"
                                        name="posProvider.customProviderName"
                                        label={<span>{i18n.t('form.customProviderName')}</span>}
                                        type="text"
                                        value={values.posProvider.customProviderName || ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.posProvider?.customProviderName}
                                        helperText={
                                            touched.posProvider?.customProviderName &&
                                            errors.posProvider?.customProviderName
                                        }
                                        className={classes.inputContainer}
                                    />
                                )}

                                {values.posProvider.providerName ===
                                    RestaurantPaymentProvider.Wallee && (
                                    <>
                                        <Input
                                            name="posProvider.identity"
                                            label={
                                                <span>
                                                    {getFieldLabel(
                                                        values.posProvider.providerName,
                                                        'identity'
                                                    )}
                                                </span>
                                            }
                                            type="text"
                                            variant="filled"
                                            error={errors.posProvider?.identity}
                                            helperText={
                                                touched.posProvider?.identity &&
                                                errors.posProvider?.identity
                                            }
                                            className={classes.inputContainer}
                                        />
                                        <Input
                                            name="posProvider.secret"
                                            label={
                                                <span>
                                                    {getFieldLabel(
                                                        values.posProvider.providerName,
                                                        'secret'
                                                    )}
                                                </span>
                                            }
                                            type="password"
                                            variant="filled"
                                            error={errors.posProvider?.secret}
                                            helperText={
                                                touched.posProvider?.secret &&
                                                errors.posProvider?.secret
                                            }
                                            className={classes.inputContainer}
                                        />
                                        <Input
                                            name="posProvider.scope"
                                            label={
                                                <span>
                                                    {getFieldLabel(
                                                        values.posProvider.providerName,
                                                        'scope'
                                                    )}
                                                </span>
                                            }
                                            variant="filled"
                                            error={errors.posProvider?.scope}
                                            helperText={
                                                touched.posProvider?.scope &&
                                                errors.posProvider?.scope
                                                    ? errors.posProvider?.scope
                                                    : undefined
                                            }
                                            className={classes.inputContainer}
                                        />
                                    </>
                                )}

                                {values.posProvider.providerName ===
                                    RestaurantPaymentProvider.Datatrans && (
                                    <>
                                        <Input
                                            name="posProvider.identity"
                                            label={
                                                <span>
                                                    {getFieldLabel(
                                                        values.posProvider.providerName,
                                                        'identity'
                                                    )}
                                                </span>
                                            }
                                            variant="filled"
                                            error={errors.posProvider?.identity}
                                            helperText={errors.posProvider?.identity}
                                            className={classes.inputContainer}
                                        />
                                        <Input
                                            name="posProvider.secret"
                                            label={
                                                <span>
                                                    {getFieldLabel(
                                                        values.posProvider.providerName,
                                                        'secret'
                                                    )}
                                                </span>
                                            }
                                            type="password"
                                            variant="filled"
                                            error={errors.posProvider?.secret}
                                            helperText={errors.posProvider?.secret}
                                            className={classes.inputContainer}
                                        />
                                    </>
                                )}
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <h6 className={classes.sectionTitle}>
                                    {i18n.t('restaurant.webProvider').toUpperCase().toUpperCase()}
                                </h6>
                                <Box mb={2} />
                                <FormControl
                                    className={classes.inputContainer}
                                    error={Boolean(
                                        touched.webProvider?.providerName &&
                                            errors.webProvider?.providerName
                                    )}
                                    fullWidth
                                >
                                    <InputLabel
                                        id="web-provider-label"
                                        style={{ marginLeft: 12, marginTop: 5 }}
                                    >
                                        {i18n.t('form.provider')}
                                    </InputLabel>
                                    <Select
                                        variant="filled"
                                        labelId="web-provider-label"
                                        id="webProvider.providerName"
                                        name="webProvider.providerName"
                                        value={values.webProvider.providerName}
                                        onChange={handleProviderChange('webProvider')}
                                        onBlur={handleBlur}
                                        autoWidth
                                    >
                                        <MenuItem value={''}>{i18n.t('form.none')}</MenuItem>
                                        <MenuItem value={RestaurantPaymentProvider.Wallee}>
                                            {RestaurantPaymentProvider.Wallee}
                                        </MenuItem>
                                        <MenuItem value={RestaurantPaymentProvider.Datatrans}>
                                            {RestaurantPaymentProvider.Datatrans}
                                        </MenuItem>
                                        {/*<MenuItem value={RestaurantPaymentProvider.Custom}>
                                            Custom
                                        </MenuItem>
                                         <MenuItem value={RestaurantPaymentProvider.PayTec}>
                                            {RestaurantPaymentProvider.PayTec}
                                        </MenuItem> */}
                                    </Select>
                                    <FormHelperText>
                                        {errors.webProvider?.providerName}
                                    </FormHelperText>
                                </FormControl>

                                {values.webProvider.providerName ===
                                    RestaurantPaymentProvider.Wallee && (
                                    <>
                                        <Input
                                            name="webProvider.identity"
                                            label={
                                                <span>
                                                    {getFieldLabel(
                                                        values.webProvider.providerName,
                                                        'identity'
                                                    )}
                                                </span>
                                            }
                                            type="text"
                                            variant="filled"
                                            error={errors.webProvider?.identity}
                                            helperText={
                                                touched.webProvider?.identity &&
                                                errors.webProvider?.identity
                                            }
                                            className={classes.inputContainer}
                                        />
                                        <Input
                                            name="webProvider.secret"
                                            label={
                                                <span>
                                                    {getFieldLabel(
                                                        values.webProvider.providerName,
                                                        'secret'
                                                    )}
                                                </span>
                                            }
                                            type="password"
                                            variant="filled"
                                            error={errors.webProvider?.secret}
                                            helperText={
                                                touched.webProvider?.secret &&
                                                errors.webProvider?.secret
                                            }
                                            className={classes.inputContainer}
                                        />
                                        <Input
                                            name="webProvider.scope"
                                            label={
                                                <span>
                                                    {getFieldLabel(
                                                        values.webProvider.providerName,
                                                        'scope'
                                                    )}
                                                </span>
                                            }
                                            variant="filled"
                                            error={errors.webProvider?.scope}
                                            helperText={errors.webProvider?.scope}
                                            className={classes.inputContainer}
                                        />
                                    </>
                                )}

                                {values.webProvider.providerName ===
                                    RestaurantPaymentProvider.Datatrans && (
                                    <>
                                        <Input
                                            name="webProvider.identity"
                                            label={
                                                <span>
                                                    {getFieldLabel(
                                                        values.webProvider.providerName,
                                                        'identity'
                                                    )}
                                                </span>
                                            }
                                            variant="filled"
                                            error={errors.webProvider?.identity}
                                            helperText={errors.webProvider?.identity}
                                            className={classes.inputContainer}
                                        />
                                        <Input
                                            name="webProvider.secret"
                                            label={
                                                <span>
                                                    {getFieldLabel(
                                                        values.webProvider.providerName,
                                                        'secret'
                                                    )}
                                                </span>
                                            }
                                            type="text"
                                            variant="filled"
                                            error={errors.webProvider?.secret}
                                            helperText={
                                                touched.webProvider?.secret &&
                                                errors.webProvider?.secret
                                            }
                                            className={classes.inputContainer}
                                        />
                                    </>
                                )}
                            </Grid>
                        </Grid>
                        <Box mt={3}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                className={classes.pymentSubmit}
                                disabled={!dirty || !isValid || isSubmitting}
                            >
                                {i18n.t('common.save')}
                            </Button>
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
}
